// Indicators of the relevé's "Positionnement · Comparaison concurrence" summary (ticket #23483, SpecTech
// Positionnement v1.0). Pure on purpose, like catchmentAreaComparison.js: this is the single source of truth
// for the formulas, unit-tested directly, while competitorPositioningService.js only gathers its inputs.
//
// Every indicator is computed on the line's PRESENT products (état présent or rupture, spec "Présent(es)"),
// only the théorique reads the listing. A line with no present product is "absente" (RG-14).

import { STATEMENT_STATE } from '@/utils/domain/catchmentAreaComparison';

export const POSITIONING_LINE = Object.freeze({
	MY_BRAND: 'myBrand',
	SUBSET: 'subset',
	COMPETITOR: 'competitor',
});

// Indicators the summary banner can report "n°1" on (RG-15). CM linéaire ranks exactly like PDL % and
// # Pr exactly like PDO % (same denominator for every line), so they would only repeat them.
export const POSITIONING_INDICATOR = Object.freeze({
	PDL: 'pdl',
	PDO: 'pdo',
	FACINGS: 'facings',
});

// Ties are decided on the value the user reads (RG-13), not on float noise below it.
export const POSITIONING_DECIMALS = 2;

// Attributes filled on a statement product (ecosVM.attributes[].entityType), as opposed to catalog ones (1).
export const STATEMENT_ATTRIBUTE_ENTITY_TYPE = 12;

const COMPETITOR_ASSORT_TYPE = 2; // productModel.assortType: 0=default, 1=promo, 2=competitors
const LISTED_SOURCE = 0; // shelfStatementProductModel.source: 0=agreement (référencé), 1=picking, 2=cross

// shelfStatementProductModel.shareOfShelfUnit (TFS#19771): which catalog dimension a facing takes on the shelf.
const SHARE_OF_SHELF_DIMENSIONS = Object.freeze({ 0: 'width', 1: 'height', 2: 'length' });

const YES_VALUES = ['true', '1', 'oui', 'yes'];

const DEFAULT_RULES = Object.freeze({
	// Product-tree node every compared product must sit under, as { depth, label }. null = no restriction.
	myBrandCategory: null,
	// "Qualis" sub-line of ma marque, as { requiredAttribute, excludedAttribute } statement attribute names.
	subset: null,
	// Statement attribute holding how many references a competitor line stands for. null = one per product.
	competitorReferenceCountAttribute: null,
});

// INNOCENTFR surveys each competitor as a single "Concurrent <brand>" product carrying its number of
// references, and only compares its Take-Home range (level-2 category "TH") — which a TH product picked in
// another relevé category (e.g. OS) still belongs to. The competitors are held to the same range.
const CUSTOMER_RULES = Object.freeze({
	INNOCENTFR: Object.freeze({
		myBrandCategory: Object.freeze({ depth: 2, label: 'TH' }),
		subset: Object.freeze({ requiredAttribute: 'PDLquali', excludedAttribute: 'ProduitDelocalise' }),
		competitorReferenceCountAttribute: 'NbRefConcurrent',
	}),
});

export function getPositioningRules(customerReference) {
	return { ...DEFAULT_RULES, ...(CUSTOMER_RULES[customerReference] || {}) };
}

function normalize(value) {
	return String(value ?? '')
		.trim()
		.toLowerCase();
}

function toNumber(value) {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : 0;
}

function roundForDisplay(value) {
	if (value == null) return null;
	const factor = 10 ** POSITIONING_DECIMALS;
	return Math.round(value * factor) / factor;
}

// A checkbox attribute (PDLquali) stores 'true'/'false', an option list (ProduitDelocalise) stores the id of
// the chosen option — so for a list, "Oui" means whichever option is labelled that way, never a raw '1'.
function resolveStatementAttribute(statementAttributes, name) {
	if (!name) return null;
	const attribute = (statementAttributes || []).find((entry) => entry?.name === name);
	if (!attribute) return null;
	const options = Array.isArray(attribute.values) ? attribute.values : [];
	const yesOptions = options.filter((option) => YES_VALUES.includes(normalize(option?.value)));
	const yesValues = options.length ? [...yesOptions.map((option) => normalize(option.id)), ...yesOptions.map((option) => normalize(option.value))] : YES_VALUES;
	return { id: attribute.id, yesValues: new Set(yesValues) };
}

function attributeValue(product, attribute) {
	if (!attribute) return null;
	return (product?.attributes || []).find((entry) => entry?.id === attribute.id)?.value ?? null;
}

function isAttributeYes(product, attribute) {
	const value = attributeValue(product, attribute);
	return value != null && attribute.yesValues.has(normalize(value));
}

// A present competitor line stands for at least one reference, even when its count was left empty or at 0.
function competitorReferenceCount(product, attribute) {
	return attribute ? Math.max(1, toNumber(attributeValue(product, attribute))) : 1;
}

function isPresent(product) {
	return product?.state === STATEMENT_STATE.PRESENT || product?.state === STATEMENT_STATE.OUT_OF_STOCK;
}

function isSurveyed(product) {
	return product?.state != null && product.state !== STATEMENT_STATE.UNKNOWN;
}

function isCompetitorProduct(product, meta) {
	return (meta?.assortType ?? product?.assortType) === COMPETITOR_ASSORT_TYPE;
}

// productTreeId defaults to 0 on both the catalog and the statement product models when unset.
function usableProductTreeId(id) {
	const numeric = Number(id);
	return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

// A catalog product's productTreeId is a leaf of Core/ProductTree (e.g. "Pur jus iPlus 750ml"): walk parentId
// up to the requested depth. A node already shallower than that depth has no ancestor there.
function ancestorLabelAtDepth(productTreesById, productTreeId, depth) {
	let node = productTreesById[productTreeId] || null;
	let guard = 0;
	while (node && Number(node.depth) > depth && guard < 50) {
		node = productTreesById[node.parentId] || null;
		guard += 1;
	}
	return node && Number(node.depth) === depth ? node.label : null;
}

// true / false whether a product sits under rules.myBrandCategory, null when its product tree is unknown.
function createCategoryScope(rules, productTreesById) {
	const category = rules.myBrandCategory;
	if (!category) return () => true;
	const expected = normalize(category.label);
	return (product, meta) => {
		const productTreeId = usableProductTreeId(meta?.productTreeId) ?? usableProductTreeId(product?.productTreeId);
		if (productTreeId == null) return null;
		return normalize(ancestorLabelAtDepth(productTreesById, productTreeId, category.depth)) === expected;
	};
}

// Ma marque occupies the dimension its facings were surveyed on (largeur/hauteur/longueur); the spec measures a
// competitor on its width only.
function productDimension(product, meta, useShareOfShelfUnit) {
	const dimension = useShareOfShelfUnit ? SHARE_OF_SHELF_DIMENSIONS[product?.shareOfShelfUnit] || 'width' : 'width';
	if (meta) return toNumber(meta[dimension]);
	return dimension === 'width' ? toNumber(product?.width) : 0;
}

// Multi-facings (TFS#8009) hold extra facings on another furnishing, sized by the furnishing itself — counted
// exactly like sfaHelper.kpi.business.shareOfShelf already does for the relevé's own PDL.
function shelfFootprint(product, meta, useShareOfShelfUnit) {
	const facing = Math.max(0, toNumber(product?.facing));
	const multiFacings = Array.isArray(product?.multiFacings) ? product.multiFacings : [];
	return {
		facings: facing + multiFacings.reduce((sum, entry) => sum + Math.max(0, toNumber(entry?.quantity)), 0),
		linear: facing * productDimension(product, meta, useShareOfShelfUnit) + multiFacings.reduce((sum, entry) => sum + toNumber(entry?.quantity) * toNumber(entry?.furnishingSize), 0),
	};
}

function createTally() {
	return { presentProducts: 0, references: 0, theoretical: 0, linear: 0, facings: 0 };
}

function addFootprint(tally, footprint) {
	tally.linear += footprint.linear;
	tally.facings += footprint.facings;
}

function share(value, total) {
	return total > 0 ? (value / total) * 100 : null;
}

function maxOf(values) {
	const usable = values.filter((value) => value != null);
	return usable.length ? Math.max(...usable) : null;
}

function tallyCategory(statement, context) {
	const { productsById, attributes, isInScope } = context;
	const myBrand = createTally();
	const subset = attributes.subsetRequired ? createTally() : null;
	const competitors = new Map();
	let hasSurveyedProduct = false;

	for (const product of statement?.products || []) {
		const meta = productsById[product?.productId] || null;

		if (isCompetitorProduct(product, meta)) {
			// A competitor product with no known product tree is kept rather than silently lost.
			if (isInScope(product, meta) === false) continue;
			const competitor = meta?.competitor || product?.competitor || null;
			const key = competitor?.id ?? meta?.competitorId ?? null;
			if (!competitors.has(key)) competitors.set(key, { competitorId: key, label: competitor?.name || '', tally: createTally() });
			hasSurveyedProduct = hasSurveyedProduct || isSurveyed(product);
			if (!isPresent(product)) continue;

			const tally = competitors.get(key).tally;
			addFootprint(tally, shelfFootprint(product, meta, false));
			tally.presentProducts += 1;
			tally.references += competitorReferenceCount(product, attributes.competitorReferenceCount);
			continue;
		}

		if (!isInScope(product, meta)) continue;
		hasSurveyedProduct = hasSurveyedProduct || isSurveyed(product);
		if (product?.source === LISTED_SOURCE) myBrand.theoretical += 1;
		if (!isPresent(product)) continue;

		const footprint = shelfFootprint(product, meta, true);
		addFootprint(myBrand, footprint);
		myBrand.presentProducts += 1;
		myBrand.references += 1;

		if (subset && isAttributeYes(product, attributes.subsetRequired) && !isAttributeYes(product, attributes.subsetExcluded)) {
			addFootprint(subset, footprint);
			subset.presentProducts += 1;
		}
	}

	return { myBrand, subset, competitors: [...competitors.values()], hasSurveyedProduct };
}

// Testeur feedback (post-v1.0): PDL is measured against the category's DECLARED shelf length
// (shelfStatementModel.size, the "linéaire" the rep enters via the ruler icon — legacy shelfSize),
// not the sum of surveyed footprints. Any gap between the two is folded into the "Autres" competitor
// line before the PDL share is computed, so every line's PDL still adds up against the declared
// length: fold the gap into that tally's linear first, then let the existing sum-based total (which
// now equals the declared length) drive every share() call unchanged. A wrong linear entry then
// surfaces as a negative "Autres" PDL instead of being silently absorbed.
function reconcileDeclaredLinear(statement, myBrand, competitors) {
	const declaredLinear = toNumber(statement?.size);
	if (declaredLinear <= 0) return null;

	const autres = competitors.find((competitor) => normalize(competitor.label) === 'autres');
	if (!autres) return null;

	const calculatedLinear = [myBrand, ...competitors.map((competitor) => competitor.tally)].reduce((sum, tally) => sum + tally.linear, 0);
	autres.tally.linear += declaredLinear - calculatedLinear;
	return autres.tally;
}

function buildCategory(statement, context) {
	const { myBrand, subset, competitors, hasSurveyedProduct } = tallyCategory(statement, context);
	// RG-04: only the relevé categories where at least one product (ma marque or a competitor) was surveyed.
	if (!hasSurveyedProduct) return null;

	const linearAdjustedTally = reconcileDeclaredLinear(statement, myBrand, competitors);

	const brandTallies = [myBrand, ...competitors.map((competitor) => competitor.tally)];
	const total = {
		linear: brandTallies.reduce((sum, tally) => sum + tally.linear, 0),
		// Also the common PDO denominator: ma marque's present references + every competitor's references.
		references: brandTallies.reduce((sum, tally) => sum + tally.references, 0),
		facings: brandTallies.reduce((sum, tally) => sum + tally.facings, 0),
	};

	function brandLine(tally) {
		const absent = tally.presentProducts === 0;
		// "Autres" carries the declared/calculated gap even with nothing surveyed under it - that's the
		// whole point (a non-zero, possibly negative, PDL flags a linear entry mistake instead of hiding it.
		const noPdl = absent && tally !== linearAdjustedTally;
		return {
			absent,
			linear: tally.linear,
			facings: tally.facings,
			present: tally.references,
			pdl: noPdl ? null : share(tally.linear, total.linear),
			pdo: absent ? null : share(tally.references, total.references),
			isTopPdl: false,
		};
	}

	const myBrandLine = {
		key: POSITIONING_LINE.MY_BRAND,
		kind: POSITIONING_LINE.MY_BRAND,
		...brandLine(myBrand),
		theoretical: myBrand.theoretical,
		dn: share(myBrand.presentProducts, myBrand.theoretical),
	};

	const competitorLines = competitors
		.map((competitor) => ({
			key: `${POSITIONING_LINE.COMPETITOR}-${competitor.competitorId ?? 'unknown'}`,
			kind: POSITIONING_LINE.COMPETITOR,
			competitorId: competitor.competitorId,
			label: competitor.label,
			...brandLine(competitor.tally),
			theoretical: null,
			dn: null,
		}))
		.filter((competitorLine) => competitorLine.label || !competitorLine.absent);

	// RG-12/13: the green n°1 marker is PDL-only, and every brand tied on the displayed value gets it.
	const rankable = [myBrandLine, ...competitorLines];
	const bestPdl = maxOf(rankable.map((line) => roundForDisplay(line.pdl)));
	if (bestPdl > 0) rankable.forEach((line) => (line.isTopPdl = roundForDisplay(line.pdl) === bestPdl));

	// RG-09: competitors by PDL % descending, absent ones (no PDL) last.
	competitorLines.sort((a, b) => (b.pdl ?? -1) - (a.pdl ?? -1) || String(a.label).localeCompare(String(b.label)));

	// RG-10: the subset is part of ma marque, so it never weighs in the totals nor the n°1 race.
	const subsetLine = subset
		? {
				key: POSITIONING_LINE.SUBSET,
				kind: POSITIONING_LINE.SUBSET,
				absent: subset.presentProducts === 0,
				linear: subset.linear,
				facings: subset.facings,
				pdl: subset.presentProducts === 0 ? null : share(subset.linear, total.linear),
				present: null,
				theoretical: null,
				dn: null,
				pdo: null,
				isTopPdl: false,
			}
		: null;

	return {
		id: statement.productTreeId,
		label: context.productTreesById[statement.productTreeId]?.label || statement.productTreeLabel || String(statement.productTreeId ?? ''),
		industrialCount: rankable.length,
		lines: [myBrandLine, subsetLine, ...competitorLines].filter(Boolean),
		total,
		myBrandRank: myBrandLine.pdl == null ? null : 1 + rankable.filter((line) => roundForDisplay(line.pdl) > roundForDisplay(myBrandLine.pdl)).length,
		myBrandTopIndicators: myBrandLine.absent ? [] : topIndicatorsOf(myBrandLine, rankable),
	};
}

function topIndicatorsOf(line, rankable) {
	const indicators = [];
	if (line.isTopPdl) indicators.push(POSITIONING_INDICATOR.PDL);
	const bestPdo = maxOf(rankable.map((entry) => roundForDisplay(entry.pdo)));
	if (bestPdo > 0 && roundForDisplay(line.pdo) === bestPdo) indicators.push(POSITIONING_INDICATOR.PDO);
	const bestFacings = maxOf(rankable.map((entry) => entry.facings));
	if (bestFacings > 0 && line.facings === bestFacings) indicators.push(POSITIONING_INDICATOR.FACINGS);
	return indicators;
}

/**
 * Builds the positioning summary of the relevé in progress, one entry per relevé category. Pure — every input
 * must already be gathered by the caller.
 *
 * @param {object} params
 * @param {Array<{productTreeId:number, productTreeLabel?:string, displayOrder?:number, size?:number, products:Array<object>}>} params.statements - the relevé's statements (one per category) with their products (productId, state, facing, source, assortType, shareOfShelfUnit, multiFacings, attributes, productTreeId) and the declared shelf length (size, legacy shelfSize)
 * @param {Record<number, object>} params.productsById - catalog products (dimensions, assortType, competitor, productTreeId)
 * @param {Record<number, {label?:string, parentId?:number, depth?:number}>} [params.productTreesById] - Core/ProductTree nodes
 * @param {Array<object>} [params.statementAttributes] - statement attribute definitions (ecosVM.attributes, entityType 12)
 * @param {object} [params.rules] - getPositioningRules(customerReference)
 * @returns {{categories: Array<object>, hasSubset: boolean}}
 */
export function buildCompetitorPositioning({ statements = [], productsById = {}, productTreesById = {}, statementAttributes = [], rules = DEFAULT_RULES }) {
	const attributes = {
		subsetRequired: resolveStatementAttribute(statementAttributes, rules.subset?.requiredAttribute),
		subsetExcluded: resolveStatementAttribute(statementAttributes, rules.subset?.excludedAttribute),
		competitorReferenceCount: resolveStatementAttribute(statementAttributes, rules.competitorReferenceCountAttribute),
	};
	const context = { productsById, productTreesById, attributes, isInScope: createCategoryScope(rules, productTreesById) };

	const categories = [...(statements || [])]
		.sort((a, b) => toNumber(a?.displayOrder) - toNumber(b?.displayOrder))
		.map((statement) => buildCategory(statement, context))
		.filter(Boolean)
		.filter((category) => category.lines.some((line) => line.kind === POSITIONING_LINE.COMPETITOR));

	return { categories, hasSubset: attributes.subsetRequired != null };
}
